import type { Image, Link } from 'mdast';
import { toString } from 'mdast-util-to-string';
import type { Parent } from 'unist';
import { visit } from 'unist-util-visit';

import { ALLOWED_VIDEO_FILE_EXTENSIONS } from '@/lib/files/file-uploads';

/**
 * remark plugin to convert Markdown links ending with .mp4 or .webm
 * into image nodes (i.e., insert a leading '!').
 *
 * Example:
 * [video.mp4](url/video.mp4)  -->  ![video.mp4](url/video.mp4)
 */
/**
 * Interface for the return type of the transformer function
 */
type TransformerFunction = {
  (tree: Parent): void;
};

/**
 * remark plugin to convert Markdown links ending with .mp4 or .webm
 * into image nodes (i.e., insert a leading '!').
 *
 * Problem:
 * Github doesn't render video elements as videos in Markdown, so we generate them as links.
 * When rendering Markdown in cubic, we want to convert these links into image nodes so they display as videos.
 *
 * Example:
 * [video.mp4](url/video.mp4)  -->  ![video.mp4](url/video.mp4)
 */
export default function remarkVideoAsImage(): TransformerFunction {
  return (tree: Parent): void => {
    // Only process URLs ending with allowed video extenstions (with optional query string)
    const extensionPattern = new RegExp(
      `\.(${ALLOWED_VIDEO_FILE_EXTENSIONS.join('|')})(?:\\?.*)?$`,
      'i',
    );
    visit(tree, 'link', (node: Link, index: number, parent: Parent) => {
      if (extensionPattern.test(node.url)) {
        // Extract the text inside the link to use as alt text
        const altText = toString(node);
        // Construct a new image node
        const imageNode: Image = {
          type: 'image',
          url: node.url,
          title: node.title || null,
          alt: altText,
        };
        // Replace the link node with the new image node
        parent.children.splice(index, 1, imageNode);
      }
    });
  };
}
